import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES, STORAGE_KEYS } from '../../constants';

const LoginForm = ({ onSuccess }) => {
  const { login, isLoggingIn, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // DeviceId, if previously remembered on this device, will be read from localStorage on submit

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, touchedFields },
    watch,
    clearErrors,
  } = useForm({
    mode: 'onChange', // Real-time validation
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Enhanced email validation regex
  const emailValidation = {
    required: 'Username is required',
  };

  // Watch form values for real-time UI updates
  const watchedEmail = watch('email');
  const watchedPassword = watch('password');

  // Handle form submission
  const onSubmit = async data => {
    // Clear any existing errors
    if (error) clearError();

    try {
      // If a deviceId has been remembered previously, include it in login
      let deviceId = null;
      try {
        deviceId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID);
      } catch (e) {
        deviceId = null;
      }

      const result = await login({
        email: data.email.trim().toLowerCase(), // Normalize email
        password: data.password,
        deviceId: deviceId,
      });

      if (result.success) {
        // Call success callback if provided
        if (onSuccess) {
          onSuccess(result);
        }
      } else if (result.requiresOtp) {
        // Redirect to OTP verification page with email and auto-send flag
        navigate(ROUTES.OTP_VERIFICATION, {
          state: {
            email: data.email.trim().toLowerCase(),
            from: location?.state?.from || { pathname: ROUTES.DASHBOARD },
            autoSendOtp: true, // Flag to indicate OTP should be auto-sent
            token: result?.data?.token,
          },
        });
      }
    } catch (err) {
      // No-op
    }
  };

  // Clear errors when user starts typing
  const handleInputChange = fieldName => {
    if (error) clearError();
    if (errors[fieldName]) clearErrors(fieldName);
  };

  // Check if field has error
  const hasFieldError = fieldName => {
    return !!errors[fieldName];
  };

  // Get field error message
  const getFieldError = fieldName => {
    return errors[fieldName]?.message;
  };

  // Check if email format looks valid (for real-time UI feedback)
  const getEmailValidationStatus = () => {
    if (!touchedFields.email || !watchedEmail) return 'neutral';
    if (errors.email) return 'error';

    // Basic format check for UI feedback
    return 'valid';
  };

  const emailStatus = getEmailValidationStatus();

  return (
    <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-md w-full space-y-8'>
        {/* Header */}
        <div className='text-center'>
          <div className='mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 shadow-lg'>
            <svg
              className='h-8 w-8 text-white'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              aria-hidden='true'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
              />
            </svg>
          </div>
          <h1 className='mt-6 text-3xl font-bold text-gray-900'>
            Sign in to your account
          </h1>
          <p className='mt-2 text-sm text-gray-600'>SKAI1 Admin Panel</p>
        </div>

        {/* Form Container */}
        <div className='bg-white rounded-xl shadow-lg p-8'>
          {/* Global Error Display */}
          {error && (
            <div
              className='mb-6 rounded-md bg-red-50 border border-red-200 p-4'
              role='alert'
            >
              <div className='flex'>
                <div className='flex-shrink-0'>
                  <svg
                    className='h-5 w-5 text-red-400'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                    aria-hidden='true'
                  >
                    <path
                      fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <div className='ml-3'>
                  <p className='text-sm font-medium text-red-800'>{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className='space-y-6'
          >
            {/* Email Field */}
            <div>
              <label
                htmlFor='email'
                className='block text-sm font-medium text-gray-700 mb-2'
              >
                Username *
              </label>
              <div className='relative'>
                <input
                  {...register('email', {
                    ...emailValidation,
                    onChange: () => handleInputChange('email'),
                  })}
                  id='email'
                  type='email'
                  autoComplete='email'
                  disabled={isLoggingIn || isSubmitting}
                  className={`
                    appearance-none relative block w-full px-4 py-3 pr-12
                    border placeholder-gray-500 text-gray-900 
                    rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 
                    focus:z-10 sm:text-sm transition-all duration-200
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${
                      hasFieldError('email')
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500 bg-red-50'
                        : emailStatus === 'valid'
                        ? 'border-green-300 focus:border-green-500 focus:ring-green-500 bg-green-50'
                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    }
                  `}
                  placeholder='Enter your email or phone'
                  aria-invalid={hasFieldError('email') ? 'true' : 'false'}
                  aria-describedby={
                    hasFieldError('email') ? 'email-error' : 'email-help'
                  }
                />

                {/* Email Status Icon */}
                <div className='absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none'>
                  {emailStatus === 'valid' && !hasFieldError('email') ? (
                    <svg
                      className='h-5 w-5 text-green-500'
                      viewBox='0 0 20 20'
                      fill='currentColor'
                      aria-hidden='true'
                    >
                      <path
                        fillRule='evenodd'
                        d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.236 4.53L7.53 10.23a.75.75 0 00-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z'
                        clipRule='evenodd'
                      />
                    </svg>
                  ) : hasFieldError('email') ? (
                    <svg
                      className='h-5 w-5 text-red-400'
                      viewBox='0 0 20 20'
                      fill='currentColor'
                      aria-hidden='true'
                    >
                      <path
                        fillRule='evenodd'
                        d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 8a1 1 0 100 2 1 1 0 000-2z'
                        clipRule='evenodd'
                      />
                    </svg>
                  ) : (
                    ''
                  )}
                </div>
              </div>

              {/* Email Error Message */}
              {hasFieldError('email') && (
                <p
                  id='email-error'
                  className='mt-2 text-sm text-red-600'
                  role='alert'
                >
                  {getFieldError('email')}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor='password'
                className='block text-sm font-medium text-gray-700 mb-2'
              >
                Password *
              </label>
              <div className='relative'>
                <input
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters long',
                    },
                    onChange: () => handleInputChange('password'),
                  })}
                  id='password'
                  type='password'
                  autoComplete='current-password'
                  disabled={isLoggingIn || isSubmitting}
                  className={`
                    appearance-none relative block w-full px-4 py-3 pr-12
                    border placeholder-gray-500 text-gray-900 
                    rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 
                    focus:z-10 sm:text-sm transition-colors duration-200
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${
                      hasFieldError('password')
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    }
                  `}
                  placeholder='Enter your password'
                  aria-invalid={hasFieldError('password') ? 'true' : 'false'}
                  aria-describedby={
                    hasFieldError('password') ? 'password-error' : undefined
                  }
                />

                {/* Password Icon */}
                <div className='absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none'></div>
              </div>

              {/* Password Error Message */}
              {hasFieldError('password') && (
                <p
                  id='password-error'
                  className='mt-2 text-sm text-red-600'
                  role='alert'
                >
                  {getFieldError('password')}
                </p>
              )}
            </div>

            {/* reCAPTCHA and Remember Me moved to OTP screen */}

            {/* Submit Button */}
            <div>
              <button
                type='submit'
                disabled={isLoggingIn || isSubmitting}
                className='
                  group relative w-full flex justify-center py-3 px-4 
                  border border-transparent text-sm font-medium rounded-lg 
                  text-white bg-gradient-to-r from-blue-600 to-indigo-600 
                  hover:from-blue-700 hover:to-indigo-700 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 
                  disabled:opacity-50 disabled:cursor-not-allowed 
                  transform transition-all duration-200 
                  hover:scale-[1.02] active:scale-[0.98]
                  shadow-lg hover:shadow-xl
                '
                aria-describedby='submit-button-description'
              >
                <span className='absolute left-0 inset-y-0 flex items-center pl-3'>
                  {isLoggingIn || isSubmitting ? (
                    <div
                      className='animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent'
                      aria-hidden='true'
                    ></div>
                  ) : (
                    ''
                  )}
                </span>

                {isLoggingIn || isSubmitting
                  ? 'Signing in...'
                  : 'Sign in to your account'}
              </button>

              <p id='submit-button-description' className='sr-only'>
                Click to sign in with your email and password
              </p>
            </div>
          </form>
        </div>

        {/* Footer */}
        <p className='text-center text-xs text-gray-500'>
          © {new Date().getFullYear()} SKAI1 Admin Panel. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
